/**
 * tutorWeekAdapter — la semaine-type du masteur (« Ma dispo »), du serveur à la grille du design system.
 *
 * Partagé : « Ma dispo », « Libérer des créneaux », et l'étape « Ma dispo » de « Compléter mon
 * profil » (`toUITutorWeek`, `pressTutorHour`).
 *
 * Portage littéral de `tutor/availability/maDispoSlot.js` (les clés et les libellés d'un créneau) et
 * de `MaDispoEditor.js` (`orderedWeekDays`, `hoursOfDay`, l'état et le libellé accessible de
 * `HourChip`), plus la durée d'engagement de `MaDispoContinuitySheet.js` (`continuityText`). Les
 * ports n'importent pas le legacy : les aides sont recopiées ici, leur source citée.
 *
 * Le serveur sert la semaine (`TutorAvailabilityResource`) : la grille réservable du régime (`grid`,
 * les heures de DÉBUT par jour), et chaque créneau porté (`slots`) — proposé, ou engagé par un live
 * (`engaged`), libéré par un geste précédent (`released`), avec les débuts des lives posés dans la
 * case (`live_times`). Toutes les règles restent au serveur.
 */
import moment from "moment"

import { weekDaysObject } from "../../../../../data/constants/activitiesConstants"
import {
    type UITutorHour,
    type UITutorHourState,
    type UITutorWeek,
    type UITutorWeekDay,
    type UITutorWeekFreshness,
} from "../../../ui/types/tutorDispoOffres"

// ── Formes lâches du serveur ────────────────────────────────────────────────

/** Un créneau de la semaine servie : « Monday 17:00-18:00 », ou un début seul (« Saturday 9 »). */
export type ApiTutorWeekSlot = {
    slot?: string | null
    state?: string | null
    engaged?: boolean | null
    released?: boolean | null
    live_times?: string[] | null
    live_session_ids?: (number | string)[] | null
}

/** `TutorAvailabilityResource`, réduit à ce que les écrans lisent. */
export type ApiTutorWeekView = {
    freshness?: string | null
    availability_starting_date?: string | null
    has_declared_slots?: boolean | null
    grid?: Record<string, string[] | null | undefined> | null
    slots?: ApiTutorWeekSlot[] | null
}

/** `TutorAvailability::STATE_*` — la seule fraîcheur qui compte ici : `verified`. */
export const AVAILABILITY_VERIFIED = "verified"

// ── Les aides de `maDispoSlot.js`, recopiées ────────────────────────────────

const parseFlatSlot = (slotString: string) => {
    const [day, ...rest] = slotString.split(" ")
    return { day, time: rest.join(" ") }
}

/** « Monday 17:00-19:00 » → « Monday ». */
export const slotDayOf = (slotString: string): string => parseFlatSlot(slotString).day

/**
 * « Monday 17:00-19:00 » → « Monday 17:00 » : le début TEL QUEL. C'est ce que l'enregistrement
 * envoie : le serveur garde la forme du créneau stocké (un « Saturday 9 » hérité n'est pas réécrit).
 */
export const slotStartOf = (slotString: string): string => {
    const { day, time } = parseFlatSlot(slotString)
    return `${day} ${time.split("-")[0]}`
}

/**
 * L'IDENTITÉ d'un créneau : son début, l'heure sur deux chiffres (« Saturday 9 » → « Saturday
 * 09:00 »), comme `TutorAvailability::slotKey()`. Toute comparaison passe par elle : la semaine porte
 * encore des débuts hérités non paddés, la grille et les lives jamais.
 */
export const slotKeyOf = (slotString: string): string => {
    const { day, time } = parseFlatSlot(slotString)
    const [hours, minutes] = time.split("-")[0].split(":")
    return `${day} ${String(hours).padStart(2, "0")}:${minutes ?? "00"}`
}

/** La grille du régime scolaire est d'une heure : un début seul vaut l'heure qui suit. */
const SLOT_DURATION_HOURS = 1

/** « 17:00 » → « 17h » ; « 08:30 » → « 8h30 ». */
export const frTime = (hhmm: string): string => {
    const [hours, minutes] = hhmm.split(":")
    const hour = String(Number(hours))
    return minutes && minutes !== "00" ? `${hour}h${minutes}` : `${hour}h`
}

const plusHours = (hhmm: string, hours: number) => {
    const [h, m] = hhmm.split(":")
    const shifted = String((Number(h) + hours) % 24).padStart(2, "0")
    return `${shifted}:${m ?? "00"}`
}

/** « 17:00 » → « 17h-18h » ; « 17:00-19:00 » → « 17h-19h » : un créneau rangé garde sa fin. */
export const timeRangeLabel = (time: string): string => {
    const [start, end] = time.split("-")
    return `${frTime(start)}-${frTime(end ?? plusHours(start, SLOT_DURATION_HOURS))}`
}

/** « Monday 17:00 » → « 17h-18h ». */
export const slotRangeLabel = (slotString: string): string => timeRangeLabel(parseFlatSlot(slotString).time)

/** `[]` → « » ; `["18:30"]` → « à 18h30 » ; `["18:00","18:30"]` → « à 18h et 18h30 », espace en tête. */
export const liveTimesLabel = (times: string[] | null | undefined): string => {
    if (!times?.length) return ""
    const labels = times.map(frTime)
    const last = labels.pop()
    return labels.length ? ` à ${labels.join(", ")} et ${last}` : ` à ${last}`
}

/**
 * Ce que dit une pilule (`chipLabelOf`) : une heure engagée se nomme par SON live quand il tombe dans
 * la case (« 18h30 » dans la case 18h-19h) ; la plage gagne pour une heure ouverte, un live à l'heure
 * pile, plusieurs lives, et quand le début du live est déjà une autre pilule du jour.
 */
export const chipLabelOf = (
    hour: { engaged: boolean; time: string; label: string; liveTimes?: string[] | null },
    dayTimes: string[] = []
): string =>
    hour.engaged &&
    hour.liveTimes?.length === 1 &&
    hour.liveTimes[0] !== hour.time &&
    !dayTimes.includes(hour.liveTimes[0])
        ? frTime(hour.liveTimes[0])
        : hour.label

/** Le nom français d'un jour de l'API. */
export const dayLabelOf = (day: string): string => (weekDaysObject as Record<string, string>)[day] ?? day

/**
 * Le jour où s'ouvre le calendrier (`startingDatePickerValue`) : la date posée, ou AUJOURD'HUI quand
 * elle est passée ou absente — le calendrier est borné à [aujourd'hui, +1 an], ouvert sur un mois
 * passé il n'offrait rien à toucher.
 */
export function startingDateOpensOn(startingDate: string | null | undefined): string {
    const stale = !startingDate || moment(startingDate).isBefore(moment().startOf("day"))
    return stale ? moment().format("YYYY-MM-DD") : (startingDate as string)
}

/**
 * La durée d'engagement d'un créneau (`continuityText`) : du 1er avril au 15 juin, « jusqu'au 30
 * juin » (fin de l'année scolaire) ; sinon trois mois. Comparé au JOUR, bornes comprises.
 */
export function continuityDuration(now: moment.Moment = moment()): string {
    const year = now.year()
    const springStart = moment(`${year}-04-01`, "YYYY-MM-DD")
    const springEnd = moment(`${year}-06-15`, "YYYY-MM-DD")
    if (now.isSameOrAfter(springStart, "day") && now.isSameOrBefore(springEnd, "day")) {
        return "jusqu'au 30 juin, fin de l'année scolaire"
    }
    return "pendant 3 mois"
}

// ── La semaine ──────────────────────────────────────────────────────────────

/** Les créneaux engagés encore tenus — les seuls que « Libérer des créneaux » peut rendre. */
export const engagedSlotsOf = (week: ApiTutorWeekView | null | undefined): ApiTutorWeekSlot[] =>
    (week?.slots ?? []).filter((slot) => slot?.slot && slot.engaged && !slot.released)

/** Les créneaux qu'un geste précédent a déjà libérés : leur live court jusqu'à sa fin. */
export const releasedSlotsOf = (week: ApiTutorWeekView | null | undefined): ApiTutorWeekSlot[] =>
    (week?.slots ?? []).filter((slot) => slot?.slot && slot.engaged && slot.released)

/** Les créneaux proposés (déclarés, sans live) de la semaine servie. */
export const openSlotsOf = (week: ApiTutorWeekView | null | undefined): string[] =>
    (week?.slots ?? []).filter((slot) => slot?.slot && !slot.engaged).map((slot) => slot.slot as string)

/** Les créneaux proposés avec un de plus, jamais deux fois la même heure (`withSlot`). */
export const withSlot = (openSlots: string[], slot: string): string[] =>
    openSlots.some((s) => slotKeyOf(s) === slotKeyOf(slot)) ? openSlots : [...openSlots, slot]

/** Les créneaux proposés sans celui-ci, comparés par leur clé. */
export const withoutSlot = (openSlots: string[], slot: string): string[] =>
    openSlots.filter((s) => slotKeyOf(s) !== slotKeyOf(slot))

/** La signature d'une liste de créneaux, insensible à l'ordre et au padding (`canonicalSlots`). */
export const canonicalSlots = (list: string[]): string => [...list].map(slotKeyOf).sort().join("|")

/**
 * Un créneau tel que la grille le dessine : proposé, engagé, libéré (barré), et dans « Libérer des
 * créneaux » marqué dans le brouillon (`releasable` : un second toucher le rend).
 */
export type TutorWeekSlot = {
    slot: string
    engaged: boolean
    freed?: boolean
    releasable?: boolean
    liveTimes?: string[] | null
}

/** Un créneau servi → un créneau de la grille. */
export const toWeekSlot = (slot: ApiTutorWeekSlot, overrides: Partial<TutorWeekSlot> = {}): TutorWeekSlot => ({
    slot: slot.slot as string,
    engaged: !!slot.engaged,
    freed: !!slot.released,
    liveTimes: slot.live_times ?? [],
    ...overrides,
})

/** Les jours de l'API, du lundi (0) au dimanche (6). */
export const WEEKDAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

/**
 * Les jours que la semaine montre, du lundi au dimanche (`orderedWeekDays`) : ceux de la grille, ET
 * un jour hors grille qui porte un créneau (un cours particulier du dimanche ne disparaît pas).
 */
function orderedWeekDays(grid: ApiTutorWeekView["grid"], slots: TutorWeekSlot[]): string[] {
    const daysWithSlots = new Set(slots.map((slot) => slotDayOf(slot.slot)))
    return WEEKDAY_ORDER.filter((day) => (grid?.[day]?.length ?? 0) > 0 || daysWithSlots.has(day))
}

type Hour = {
    time: string
    label: string
    slot: TutorWeekSlot | null
    engaged: boolean
    freed: boolean
    liveTimes: string[]
}

/**
 * Les heures d'UN jour, dans l'ordre de l'horloge (`hoursOfDay`) : la grille fusionnée avec ce que le
 * tuteur porte — un créneau déclaré et son jumeau de la grille sont UNE pilule.
 */
function hoursOfDay(day: string, grid: ApiTutorWeekView["grid"], slots: TutorWeekSlot[]): Hour[] {
    const byTime = new Map<string, Hour>()
    for (const time of grid?.[day] ?? []) {
        byTime.set(time, { time, label: timeRangeLabel(time), slot: null, engaged: false, freed: false, liveTimes: [] })
    }
    for (const slot of slots) {
        if (slotDayOf(slot.slot) !== day) continue
        const time = slotKeyOf(slot.slot).split(" ")[1]
        byTime.set(time, {
            time,
            label: slotRangeLabel(slot.slot),
            slot,
            engaged: slot.engaged,
            freed: !!slot.freed,
            liveTimes: slot.liveTimes ?? [],
        })
    }
    // Padded des deux côtés : l'ordre des chaînes est celui de l'horloge.
    return [...byTime.values()].sort((a, b) => a.time.localeCompare(b.time))
}

const stateOf = (hour: Hour): UITutorHourState =>
    hour.freed ? "freed" : hour.engaged ? "live" : hour.slot != null ? "taken" : "open"

/** Le libellé accessible de `HourChip` : l'heure de la case ET le live qu'elle porte. */
function accessibilityLabelOf(day: string, hour: Hour): string {
    const suffix = hour.freed
        ? " — libéré"
        : hour.engaged
          ? ` — occupé par un live${liveTimesLabel(hour.liveTimes)}`
          : hour.slot != null
            ? " — dispo"
            : ""
    return `${dayLabelOf(day)} ${hour.label}${suffix}`
}

/**
 * La semaine que la grille dessine (`MaDispoWeekGrid`). L'`id` d'une heure est la clé du créneau
 * (« Monday 17:00 ») : c'est ce que l'écran ajoute au brouillon, retire, ou marque.
 */
export function toUITutorWeekDays(grid: ApiTutorWeekView["grid"], slots: TutorWeekSlot[]): UITutorWeekDay[] {
    return orderedWeekDays(grid, slots).map((day) => {
        const hours = hoursOfDay(day, grid, slots)
        const dayTimes = hours.map((hour) => hour.time)
        return {
            id: day,
            label: dayLabelOf(day),
            slots: hours.map((hour): UITutorHour => ({
                id: `${day} ${hour.time}`,
                label: chipLabelOf(hour, dayTimes),
                state: stateOf(hour),
                accessibilityLabel: accessibilityLabelOf(day, hour),
                releasable: hour.slot?.releasable || undefined,
                liveTimesLabel: hour.engaged ? liveTimesLabel(hour.liveTimes) : undefined,
            })),
        }
    })
}

/**
 * Le bandeau (`bannerVariant` de `MaDispoScreen`) : vert pour une semaine confirmée et sans
 * brouillon, « ajoute » quand le serveur dit qu'aucun créneau n'est déclaré, « confirme » sinon.
 */
export function tutorWeekFreshness(week: ApiTutorWeekView | null | undefined, dirty: boolean): UITutorWeekFreshness {
    if (week?.freshness === AVAILABILITY_VERIFIED && !dirty) return "verified"
    return week?.has_declared_slots ? "toConfirm" : "needsSlots"
}

// ── Ce que « Ma dispo » et l'étape « Ma dispo » du profil partagent ─────────

/**
 * La semaine que l'éditeur dessine (`<TutorWeekEditor>`, `<TutorAvailabilityPage>`) : la semaine
 * servie, ses créneaux proposés remplacés par ceux du brouillon (`openSlots`) ; un créneau libéré se
 * montre barré. Partagée par « Ma dispo » et l'étape « Ma dispo » de « Compléter mon profil »
 * (`TutorAvailabilityStep`, qui tient le brouillon dans son formulaire).
 */
export function toUITutorWeek(week: ApiTutorWeekView, openSlots: string[]): UITutorWeek {
    const slots = [
        ...openSlots.map((slot) => ({ slot, engaged: false })),
        ...engagedSlotsOf(week).map((slot) => toWeekSlot(slot)),
        ...releasedSlotsOf(week).map((slot) => toWeekSlot(slot)),
    ]
    return { startingDate: week.availability_starting_date ?? null, days: toUITutorWeekDays(week.grid, slots) }
}

/**
 * Ce que fait le toucher d'une heure sur le brouillon (`MaDispoEditor` → `HourChip`) : une heure libre
 * se propose (`added`), une heure proposée se retire (`removed`) — sans confirmation, le brouillon se
 * défait d'un toucher ; une heure avec live ne bouge pas (`live` : sa boîte) ; une heure libérée est
 * inerte (`none`). C'est à l'appelant de dire l'engagement à la première heure posée.
 */
export function pressTutorHour(
    openSlots: string[],
    hour: UITutorHour
): { openSlots: string[]; effect: "added" | "removed" | "live" | "none" } {
    if (hour.state === "live") return { openSlots, effect: "live" }
    if (hour.state === "freed") return { openSlots, effect: "none" }
    if (hour.state === "taken") return { openSlots: withoutSlot(openSlots, hour.id), effect: "removed" }
    return { openSlots: withSlot(openSlots, hour.id), effect: "added" }
}
